import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompanyService } from '@app/features/companies/company.service';
import {
  SchedulesForm,
  SchedulesFormValue,
} from '@app/features/contracts/embauche-dialog/embauche-tabs-form/schedules-form/schedules-form';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { SaveButton } from '@app/shared/buttons/save-button/save-button';
import { ColorPickerDirective } from 'ngx-color-picker';
import { BehaviorSubject, catchError, map, of } from 'rxjs';
import { PostalAddressForm } from '../../../shared/forms/postal-address-form/postal-address-form';
import { CompanyLogoService } from '../company-logo.service';
import { Company, companyStateLabel } from '../company.model';
import { LegalInformationForm } from '../legal-information-form/legal-information-form';
import { PostalAddressFormGroup } from '../postal-adress.model';

export interface CompanyDialogData {
  companyId: string;
}

@Component({
  selector: 'app-company-dialog',
  imports: [
    CommonModule,
    SchedulesForm,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    SaveButton,
    LegalInformationForm,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    ColorPickerDirective,
    MatIconModule,
    MatTooltipModule,
    PostalAddressForm,
  ],
  templateUrl: './company-dialog.html',
  styleUrls: ['./company-dialog.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * La fiche d'une société. Le registre des entités légales tient sa raison sociale, sa forme
 * juridique, son siège, son SIREN, son SIRET, son RCS, son dossier du pôle social, son état et son
 * logo : la fiche les montre sans les modifier, et ne modifie que ce que CDT tient seul
 * (RG-societes-lues-dans-les-entites-legales). Les sociétés se créent dans le registre.
 */
export class CompanyDialog implements OnInit, AfterViewInit {
  readonly cdr = inject(ChangeDetectorRef);
  readonly dialogRef = inject(MatDialogRef<CompanyDialog>);
  readonly data = inject<CompanyDialogData>(MAT_DIALOG_DATA);
  readonly companyService = inject(CompanyService);
  readonly companyLogoService = inject(CompanyLogoService);
  readonly company: Company = this.companyService.findById(this.data.companyId);
  readonly stateLabel = companyStateLabel(this.company);
  readonly hasCompanyLogo$ = new BehaviorSubject<boolean>(false);
  readonly authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);

  initSchedulesFormValue: SchedulesFormValue = {
    collectiveSchedules: false,
    schedules: this.company.collectiveSchedules,
  };
  color: string = this.company.companyColor || 'white';
  phoneNumberControl = new FormControl(this.company.phoneNumber || '', {
    nonNullable: true,
    validators: [Validators.required],
  });
  emailControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.email],
  });
  /** Tenu par le registre des entités légales (RG-dossier-du-pole-social) : affiché seulement. */
  vanexNetworkFolderNameControl = new FormControl(
    { value: this.company.vanexNetworkFolderName ?? '', disabled: true },
    { nonNullable: true },
  );
  retraiteComplementaireForm: PostalAddressFormGroup = new FormGroup({
    name: new FormControl(this.company.retraiteComplementaire?.name || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    address: new FormControl(this.company.retraiteComplementaire?.address || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    additionalAddress: new FormControl(this.company.retraiteComplementaire?.additionalAddress, {
      nonNullable: true,
    }),
    postalCode: new FormControl(this.company.retraiteComplementaire?.postalCode || '', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^\d{5}$/)],
    }),
    city: new FormControl(this.company.retraiteComplementaire?.city || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });
  prevoyanceForm: PostalAddressFormGroup = new FormGroup({
    name: new FormControl(this.company.prevoyance?.name || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    additionalAddress: new FormControl(this.company.prevoyance?.additionalAddress, {
      nonNullable: true,
    }),
    address: new FormControl(this.company.prevoyance?.address || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    postalCode: new FormControl(this.company.prevoyance?.postalCode || '', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^\d{5}$/)],
    }),
    city: new FormControl(this.company.prevoyance?.city || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  toggleColorPicker = false;
  isReadOnly = false;

  @ViewChild(SchedulesForm) schedulesForm?: SchedulesForm;
  @ViewChild(LegalInformationForm) legalInformationForm?: LegalInformationForm;
  @ViewChild(SaveButton) saveButton!: SaveButton;

  get isFormValid(): boolean {
    return (
      (this.schedulesForm?.form.valid ?? false) &&
      (this.legalInformationForm?.form.valid ?? false) &&
      this.phoneNumberControl.valid &&
      this.emailControl.valid &&
      this.retraiteComplementaireForm.valid &&
      this.prevoyanceForm.valid
    );
  }

  ngOnInit() {
    this.emailControl.setValue(this.company.email ?? '');
    this.phoneNumberControl.setValue(this.company.phoneNumber ?? '');
    this.companyLogoService
      .findById(this.company.id)
      .pipe(
        map(() => true),
        catchError(() => of(false)),
      )
      .subscribe((hasLogo) => {
        this.hasCompanyLogo$.next(hasLogo);
        this.cdr.markForCheck();
      });
    this.isReadOnly = !this.authenticatedUserService.hasAnyRole([
      'cdt-directeur-rh',
      'cdt-responsable-rh',
    ]);
  }

  ngAfterViewInit(): void {
    this.initEnability();
  }

  initEnability() {
    if (this.isReadOnly) {
      this.legalInformationForm?.form.disable();
      this.phoneNumberControl.disable();
      this.emailControl.disable();
      this.retraiteComplementaireForm.disable();
      this.prevoyanceForm.disable();
      this.schedulesForm?.form.disable();
      this.cdr.markForCheck();
    }
  }

  onSave() {
    if (!this.isFormValid) {
      return;
    }
    const companySchedules = this.schedulesForm!.form.getRawValue().schedules;
    const legalInformation = this.legalInformationForm!.form.getRawValue();
    const retraiteComplementaire = this.retraiteComplementaireForm.getRawValue();
    const prevoyance = this.prevoyanceForm.getRawValue();

    // Ce que tient le registre part tel qu'il a été lu : l'API ne le modifie pas.
    const companyUpdate: Company = {
      ...this.company,
      companyColor: this.color,
      email: this.emailControl.value,
      phoneNumber: this.phoneNumberControl.value,
      collectiveSchedules: companySchedules,
      legalInformation,
      retraiteComplementaire,
      prevoyance,
    };
    this.update(companyUpdate);
  }

  private update(company: Company) {
    this.saveButton.startSaving();
    this.companyService.update(company).subscribe({
      next: (updatedCompany) => {
        this.saveButton.stopSaving(true);
        this.dialogRef.close(updatedCompany);
      },
      error: (error) => {
        console.error('Error updating company:', error);
        this.saveButton.stopSaving(false);
      },
    });
  }
}
